/**
 * Utility functions to check if an exam has ended based on endDate field
 * This prevents sending backend updates for exams that have already ended
 */

/**
 * Check if an exam has ended based on the endDate field
 * @param {Object} examData - The exam data containing endDate and status
 * @returns {boolean} True if the exam has ended (either by status or endDate), false otherwise
 */
export const isExamEnded = (examData) => {
  if (!examData) {
    console.log('[ExamEndChecker] No exam data provided');
    return false;
  }

  // First check the status field (existing logic)
  if (examData.status === 'Finished') {
    console.log('[ExamEndChecker] Exam ended by status: Finished');
    return true;
  }

  // Check if exam has ended based on endDate field
  if (examData.endDate || examData.EndDate) {
    const endDate = examData.endDate || examData.EndDate;
    const now = new Date();
    const examEndDate = new Date(endDate);
    
    if (isNaN(examEndDate.getTime())) {
      console.warn('[ExamEndChecker] Invalid endDate format:', endDate);
      return false;
    }

    const hasEndDatePassed = now >= examEndDate;
    
    if (hasEndDatePassed) {
      console.log('[ExamEndChecker] Exam ended by endDate:', {
        endDate: endDate,
        currentTime: now.toISOString(),
        examEndTime: examEndDate.toISOString(),
        timePassedBy: Math.floor((now - examEndDate) / 1000) + ' seconds'
      });
      return true;
    }
  }

  return false;
};

/**
 * Check if backend updates should be prevented for the given exam
 * This combines status and endDate checks
 * @param {Object} examData - The exam data containing endDate and status
 * @param {string} updateType - Type of update being attempted (for logging)
 * @returns {boolean} True if updates should be prevented, false if updates are allowed
 */
export const shouldPreventBackendUpdates = (examData, updateType = 'unknown') => {
  const examEnded = isExamEnded(examData);
  
  if (examEnded) {
    console.log(`[ExamEndChecker] Preventing ${updateType} update - exam has ended`);
    return true;
  }
  
  return false;
};

/**
 * Wrapper function to conditionally execute backend update functions
 * Only executes the update if the exam hasn't ended
 * @param {Object} examData - The exam data containing endDate and status  
 * @param {Function} updateFunction - The function to execute if exam hasn't ended
 * @param {string} updateType - Type of update being attempted (for logging)
 * @returns {Promise|null} Result of updateFunction if executed, null if prevented
 */
export const conditionalBackendUpdate = async (examData, updateFunction, updateType = 'unknown') => {
  if (shouldPreventBackendUpdates(examData, updateType)) {
    console.log(`[ExamEndChecker] Skipping ${updateType} - exam has ended`);
    return null;
  }
  
  try {
    console.log(`[ExamEndChecker] Executing ${updateType} - exam is still active`);
    return await updateFunction();
  } catch (error) {
    console.error(`[ExamEndChecker] Error during ${updateType}:`, error);
    throw error;
  }
};
